import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  CircularProgress,
  Stack,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from '@mui/material';
import { Typography } from 'components/index';
import { FileUploadDTO } from '../../common/utils/util.type';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { useQuery } from 'react-query';
import keys from '../../common/react-query/data-keys';
import { getFileUploadUrl, getProjectFiles } from '../project/project.api';
import { toast } from 'react-toastify';
import { formatBytes, joinDateAndUser } from '../../common/utils/common.util';
import ExcelIcon from '../../assets/img/excel_icon.png';
import PdfIcon from '../../assets/img/pdf_icon.png';
import FileIcon from '../../assets/img/file_icon.png';

interface Props {
  projectId: string;
}

const FilesAccordion: React.FC<Props> = ({ projectId }) => {
  const { data: files, isLoading, isError, error } = useQuery<FileUploadDTO[]>([keys.files, projectId], () => getProjectFiles(projectId));
  const [downloadingFileBlobs, setDownloadingFileBlobs] = useState<string[]>([]);

  const sortedFiles = React.useMemo(
    () => files?.slice().sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()),
    [files],
  );

  if (isError) {
    console.error('Error fetching project files', error);
    toast.error('Error fetching project files');
    return null;
  }

  const handleDownload = async (file: FileUploadDTO) => {
    setDownloadingFileBlobs([...downloadingFileBlobs, file.blobName]);
    const url = await getFileUploadUrl(file.blobName);
    window.open(url, '_blank');
    setDownloadingFileBlobs(downloadingFileBlobs.filter(blob => blob !== file.blobName));
  };

  return (
    <Accordion elevation={3} defaultExpanded style={{ marginTop: '5px', borderRadius: '8px' }}>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography variant='h5' color={'primary'}>
          Project Files
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ width: '100%' }}>
          {isLoading && (
            <Stack alignItems={'center'}>
              <CircularProgress />
            </Stack>
          )}
          {!isLoading && (!sortedFiles || sortedFiles.length === 0) ? <Typography textAlign={'center'}>No Files Found</Typography> : null}
          <List sx={listStyles}>
            {sortedFiles?.map((file, index) => (
              <Box key={index} sx={listItemBoxStyles}>
                <ListItem alignItems='flex-start'>
                  <ListItemAvatar>
                    <Avatar variant='rounded' src={getFileIconSrc(file.fileName)}></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={file.fileName}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 'bold',
                    }}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleDownload(file)}
                    secondary={
                      <React.Fragment>
                        <Typography variant='button' fontStyle='italic'>
                          {formatBytes(file.fileSize)}
                        </Typography>
                        <br />
                        <Typography variant='caption'>
                          {'Uploaded on '}
                          {file.createdBy && joinDateAndUser(file.createdOn, file.createdBy)}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const listStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
  gap: 6,
  width: '100%',
  bgcolor: 'background.paper',
  padding: 2,
} as const;

const listItemBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 2,
  padding: 2,
} as const;

const getFileIconSrc = (fileName: string) => {
  const extension = fileName.split('.').pop() ?? '';
  if (extension === 'pdf') {
    return PdfIcon;
  } else if (['xls', 'xlsx', 'xlsb', 'xltm', 'xltx', 'xlsm', 'csv'].includes(extension)) {
    return ExcelIcon;
  }
  return FileIcon;
};

export default FilesAccordion;
